import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

import { Event, Feature, Team, WizardStep } from '@reg/shared/model';
import { AuthService, ConfigurationService, DivisionService, EventService, ProductService } from '@reg/shared/service';

/**
 * Service used to hold and determine what wizard steps should be displayed.
 * This class should also be used to determine where the "Previous" and "Continue" (next)
 * buttons should navigate to.
 */
@Injectable()
export class WizardStepService {
    public static TYPE_REGISTRATION: string = 'registration';
    public static TYPE_EVENT_SETUP: string = 'event-setup';

    public event: Event;
    public team: Team;
    public obsWizardSteps$: Observable<WizardStep[]>;
    private obsWizardSteps: BehaviorSubject<WizardStep[]> = new BehaviorSubject<WizardStep[]>([]);
    private paymentName: string = 'payment';
    private registrantsName: string = 'registrants';
    private wizardType: string;
    private isCollectingMoneyOnline: boolean;

    /**
     * Determines what the button text should be for the next/finish button
     */
    get nextButtonText(): string {
        if (this.isLastWizardStep()) {
            return 'Finish';
        }
        return 'Continue';
    }

    /**
     * Constructor
     */
    constructor(
                private router: Router,
                private authService: AuthService,
                private configurationService: ConfigurationService,
                private eventService: EventService,
    ) {
        this.obsWizardSteps$ = this.obsWizardSteps.asObservable();
    }

    /**
     * Update the wizard steps based on the event data and features.
     * This will trigger the obsWizardSteps$ to trigger an event to it's
     * subscribers will know to update them selves (e.g. WizardStepComponent)
     */
    public updateRegistrationWizardSteps(event: Event, params: string[] = []): WizardStep[] {
        this.event = event;
        this.wizardType = WizardStepService.TYPE_REGISTRATION;

        if (!event) {
            return [];
        }

        const steps: WizardStep[] = [];
        const isRegistration: boolean = event.hasFeature(Feature.REGISTRATION_ID);

        if (!isRegistration) {
            console.error('The event does not offer registration.');
        }

        const participantName: string = this.configurationService.getSportParticipantNamePlural();

        steps.push(new WizardStep(
            'Role',
            '',
            ['/event', this.event.id, 'registration'],
            true,
        ));

        steps.push(new WizardStep(
            participantName,
            this.registrantsName,
            ['/event', this.event.id, 'registration', this.registrantsName].concat(params),
            true,
        ));

        if (event.products && event.products.length > 0) {
            steps.push(new WizardStep(
                'Add-Ons',
                'add-ons',
                ['/event', this.event.id, 'registration', 'add-ons'].concat(params),
                true,
            ));
        }

        steps.push(new WizardStep(
            'Payment',
            'payment',
            ['/event', this.event.id, 'registration', 'payment'].concat(params),
            true,
        ));

        // Disabling FloPro step until https://flocasts.atlassian.net/browse/ARENA-3066
        // if (this.authService.user &&
        //     !this.authService.user.isFloProMember) {
        //     steps.push(new WizardStep(
        //         'FloPRO',
        //         'flopro',
        //         ['/event', this.event.id, 'registration', 'flopro'].concat(params),
        //         true));
        // }

        steps.push(new WizardStep(
            'Receipt',
            'receipt',
            ['/event', this.event.id, 'registration', 'receipt'].concat(params),
            true,
        ));

        this.obsWizardSteps.next(steps);

        return steps;
    }

    /**
     * Update the wizard steps based on the event data and features.
     * This will trigger the obsWizardSteps$ to trigger an event to it's
     * subscribers will know to update them selves (e.g. WizardStepComponent)
     */
    public updateEventSetupWizardSteps(event: Event): WizardStep[] {
        this.event = event;
        this.wizardType = WizardStepService.TYPE_EVENT_SETUP;

        if (!event) {
            return [];
        }

        const isEventMgmt: boolean = event.hasFeature(Feature.EVENT_MGMNT_ID);
        const isRegistration: boolean = event.hasFeature(Feature.REGISTRATION_ID);
        const steps: WizardStep[] = [];

        steps.push(new WizardStep('Basic Info', '', ['/event', this.event.id, 'setup'], true));
        steps.push(new WizardStep(
            'Contacts',
            'contacts',
            ['/event', this.event.id, 'setup', 'contacts'],
            isEventMgmt || isRegistration,
        ));
        steps.push(new WizardStep(
            'Divisions',
            'divisions',
            ['/event', this.event.id, 'setup', 'divisions'],
            isEventMgmt || isRegistration,
        ));
        steps.push(new WizardStep(
            'Registration',
            'registrations',
            ['/event', this.event.id, 'setup', 'registrations'],
            isRegistration,
        ));
        steps.push(new WizardStep('Add-Ons', 'add-ons', ['/event', this.event.id, 'setup', 'add-ons'], isRegistration));

        const displayBankAccount: boolean = !!this.event.stripeAccount.id || this.isCollectingMoneyOnline;

        steps.push(new WizardStep(
            'Bank Account',
            'bank-account',
            ['/event', this.event.id, 'setup', 'bank-account'],
            displayBankAccount,
        ));

        // Determine if the event is going to be collecting money online, if so we need to
        // display the Bank Account wizard step
        if (!displayBankAccount && !this.event.isNew && this.event.hasRegistration()) {
            this.eventService.getIsCollectingMoneyOnline(this.event.id)
                .subscribe((isCollectingMoneyOnline: boolean) => {
                    this.isCollectingMoneyOnline = isCollectingMoneyOnline;

                    if (isCollectingMoneyOnline) {
                        this.updateEventSetupWizardSteps(this.event);
                    }
                });
        }

        this.obsWizardSteps.next(steps);

        return steps;
    }

    /**
     * Determines if the user is on the last wizard step
     */
    public isLastWizardStep(): boolean {
        return this.getNextStep() === null;
    }

    /**
     * Navigate to the next step. If there are not more steps navigation will
     * go to the configured 'wizardFinishRedirectUrl'
     */
    public navigateToNextStep(): void {
        const nextStep: WizardStep = this.getNextStep();

        if (nextStep) {
            this.router.navigate(nextStep.routeParams);
        } else if (this.wizardType === WizardStepService.TYPE_EVENT_SETUP) {
            // User is in event setup and is at the end of the wizard so take them
            // to the event dashboard next
            if (!this.event.isSetupComplete) {
                // Mark the isSetupComplete flag to true
                this.event.isSetupComplete = true;
                this.eventService.save(this.event).subscribe(() => {
                    this.navigateToEventDashboard(this.event.id);
                });
            } else {
                this.navigateToEventDashboard(this.event.id);
            }
        }
    }

    public navigateToEventDashboard(eventId: string): void {
        this.router.navigate(['/event', eventId, 'admin', 'dashboard']);
    }

    /**
     * Navigate to the registrants step
     */
    public navigateToRegistrantsStep(): void {
        if (this.obsWizardSteps.getValue().length > 0) {
            const registrants: WizardStep = this.obsWizardSteps.getValue().filter(
                (x: WizardStep) => x.routeName === this.registrantsName,
            )[0];
            this.router.navigate(registrants.routeParams);
        }
    }

    /**
     * Navigate to the last step
     */
    public navigateToLastStep(): void {
        if (this.obsWizardSteps.getValue().length > 0) {
            const lastStepIdx: number = this.obsWizardSteps.getValue().length - 1;
            this.router.navigate(this.obsWizardSteps.getValue()[lastStepIdx].routeParams);
        }
    }

    /**
     * Navigate to the previous step
     */
    public navigateToPreviousStep(): void {
        const prevStep: WizardStep = this.getPreviousStep();

        if (prevStep) {
            this.router.navigate(prevStep.routeParams);
        }
    }

    /**
     * Navigate to payment step
     */
    public navigateToPaymentStep(): void {
        if (this.obsWizardSteps.getValue().length > 0) {
            const payment: WizardStep = this.obsWizardSteps.getValue()
                .filter((x: WizardStep) => x.routeName === this.paymentName)[0];
            this.router.navigate(payment.routeParams);
        }
    }

    /**
     * Gets the next step
     */
    public getNextStep(): WizardStep {
        let nextStep: WizardStep = null;
        const steps: WizardStep[] = this.obsWizardSteps.getValue();

        steps.forEach((step: WizardStep, idx: number) => {
            if (step.isActive &&
                step.isVisible &&
                steps.length >= idx + 1) {
                nextStep = steps.filter((step2: WizardStep, idx2: number) => idx2 > idx && step2.isVisible)[0];
            }
        });

        if (!nextStep) {
            nextStep = null;
        }

        return nextStep;
    }

    /**
     * Gets the previous step
     */
    public getPreviousStep(): WizardStep {
        let prevStep: WizardStep = null;
        let prevVisibleStep: WizardStep = null;
        const steps: WizardStep[] = this.obsWizardSteps.getValue();

        steps.forEach((step: WizardStep, idx: number) => {
            if (step.isActive &&
                step.isVisible) {
                // We found our previous step
                prevStep = prevVisibleStep;
            }

            if (step.isVisible &&
                prevStep === null) {
                // The prevStep has not been assigned yet
                // so hold onto this step as the previous
                prevVisibleStep = step;
            }
        });

        if (!prevStep) {
            prevStep = null;
        }

        return prevStep;
    }
}
